import debounce from 'lodash/debounce';
import React, { useEffect, useRef } from 'react';
import { MdOutlineHelp, MdCheckCircle } from 'react-icons/md';
import styled from 'styled-components/macro';

import FramiButton from '../../common/FramiButton';
import FramiIconButton from '../../common/FramiIconButton';
import FramiMenu from '../../common/FramiMenu';
import FramiHamburger from '../../common/navigation/FramiHamburger';
import { FilterIcon } from '../../../icons';
import {
    useAppContext,
    useComposition,
    useCompositionTemplates,
    useNavigation,
    useOrder,
    usePromotion,
} from '../../../context';
import { Title } from './styled';
import { uiColors } from '../../../common/styles';
import { CircularProgress } from '@mui/material';
import { useImages } from '../../../context';
import { CHECKOUT, VOUCHER } from '../../../providers/NavigationProvider';
import { useIntl } from 'react-intl';
import FramiCountdownTimer from '../../common/FramiCountdownTimer';
import AddToCartButton from '../AddToCartButton';
// import dayjs from 'dayjs';

const Wrapper = styled.div`
    width: 100%;
    max-height: ${(props) => (props.$extendHeight ? '150px' : '89px')};
    display: flex;
    flex-direction: column;
    justify-content: ${(props) => (props.$componentAsTitle ? 'center' : '')};
    box-shadow: ${(props) => (props.$hasChildren ? '' : props.$showShadows ? '0 5px 5px -5px rgba(0, 0, 0, 0.1)' : '')};
    padding: ${(props) =>
        props.$isSmall
            ? '0 10px'
            : props.$hasChildren
              ? `8px 16px 0px`
              : props.$componentAsTitle
                ? '8px 0px'
                : '8px 16px'};
    transition: all 0.2s ease-in-out;
`;

const TitleSection = styled.section`
    display: flex;
    gap: 5px;
    width: 100%;
    white-space: nowrap;
    align-items: center;
    z-index: 4;
    background-color: white;
`;

const DiscountSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 10px 0;
    font-weight: 500;
    width: fit-content;
    padding: ${(props) => (props.$isMobile ? '5px 8px' : '5px 15px')};
    border: 3px solid ${uiColors.PROMO};
    align-self: center;
    text-align: center;
    cursor: ${props => props.onClick ? 'pointer' : 'unset'};
    text-decoration: ${props => props.onClick ? 'underline' : 'unset'};
`;

const DrawerHeader = ({
    title,
    hideButton,
    tooltipHelpTitle,
    showSortingOptions,
    showShadows,
    fontSize,
    onClose,
    additionalButton,
    className,
    children,
}) => {
    const { activeDrawer, drawerStack, setDrawerHeaderHeight, closeDrawer, openDrawer } = useNavigation();
    const { sortOptions } = useCompositionTemplates();
    const { isMobile, smallHeightDevice, isHandheldDevice } = useAppContext();
    const { imagesLoading } = useImages();
    const { hidePromo, isPromotionActive, promotion } = usePromotion();
    const { getOrCreateVoucher } = useComposition();
    const { order } = useOrder();
    const intl = useIntl();

    if (hideButton) {
        // do nothing
    } else if (isMobile || drawerStack.length > 1) {
        hideButton = false;
    } else {
        hideButton = true;
    }

    const sectionRef = useRef(null);

    useEffect(() => {
        const debouncedSetDrawerHeaderHeight = debounce((height) => {
            setDrawerHeaderHeight(height);
        }, 200);

        const observer = new ResizeObserver((entries) => {
            if (entries[0] && entries[0].borderBoxSize && entries[0].borderBoxSize[0]) {
                const { blockSize } = entries[0].borderBoxSize[0];
                debouncedSetDrawerHeaderHeight(blockSize);
            } else {
                debouncedSetDrawerHeaderHeight(0);
            }
        });
        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }
        return () => {
            observer.disconnect();
        };
    }, [title, children, setDrawerHeaderHeight, isMobile, smallHeightDevice, activeDrawer]);

    return (
        title && (
            <Wrapper
                className={className}
                ref={sectionRef}
                $isMobile={isHandheldDevice}
                $isSmall={smallHeightDevice}
                $hasChildren={!!children}
                $componentAsTitle={typeof title !== 'string'}
                $showShadows={showShadows}
                $extendHeight={activeDrawer === CHECKOUT}
            >
                {activeDrawer === CHECKOUT && promotion?.coupon && isPromotionActive && !order?.compositions?.every((c) => c.is_voucher) && (
                    <DiscountSection $isMobile={isMobile}
                    // onClick={() => {
                    //     getOrCreateVoucher();
                    //     if (isMobile) {
                    //         openDrawer(VOUCHER, isMobile);
                    //     }
                    // }}
                    >
                        <p>
                           { intl.formatMessage({
                                id: `studio.workspace.message.discount.promotion.discount` },
                                {
                                    // promotion: PROMOTION_NAME,
                                    // separator: <br />,
                                    code: promotion.coupon.code,
                                    percentage: promotion.coupon.percentage_off,
                            //         endDate: dayjs(promotion.end_date).format('L').replace(
                            // /[/.-]*2025[/.-]*/g, ''
                            //         ),
                                }
                            )}
                            <br/>
                            <FramiCountdownTimer targetDate={promotion.end_date} onCountdownEnd={hidePromo} />
                        </p>
                    </DiscountSection>
                )}
                {typeof title === 'string' ? (
                    <TitleSection>
                        <Title $fontSize={fontSize ?? 20}>
                            <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                {title}
                                {tooltipHelpTitle && (
                                    <FramiIconButton
                                        showTooltip
                                        title={tooltipHelpTitle}
                                        icon={<MdOutlineHelp fill={uiColors.GRAY_200} size={18} />}
                                    />
                                )}
                                {imagesLoading && <CircularProgress size={20} style={{ marginLeft: 10 }} />}
                            </span>
                        </Title>

                        {showSortingOptions && (
                            <FramiMenu options={sortOptions}>
                                {(openMenu) => (
                                    <FramiIconButton
                                        className={`primary-color`}
                                        title={'sorting options'}
                                        $isMobile={isMobile}
                                        icon={<FilterIcon />}
                                        size={25}
                                        onClick={openMenu}
                                        dataCy="sorting-options-button"
                                    />
                                )}
                            </FramiMenu>
                        )}
                        {additionalButton && (
                            <FramiButton
                                style={{ margin: 0, padding: '4px 6px' }}
                                dataPh={additionalButton.dataPh}
                                variant="secondary"
                                leftIcon={additionalButton.icon}
                                title={additionalButton.title}
                                onClick={additionalButton.onClick}
                                dataCy={additionalButton.dataCy}
                            />
                        )}
                        {!hideButton && !smallHeightDevice && (
                            <FramiButton
                                className={'ok-button'}
                                dataPh={'ok button'}
                                title={intl.formatMessage({ id: 'studio.drawer.close' })}
                                dataCy="ok-button"
                                variant="primary"
                                // rightIcon={<MdCheckCircle size={20} />}
                                onClick={onClose ?? closeDrawer}
                            />
                        )}
                        {isMobile && activeDrawer === VOUCHER && (
                            <AddToCartButton />
                        )}
                        {title && smallHeightDevice && <FramiHamburger smallDevice={smallHeightDevice} />}
                    </TitleSection>
                ) : (
                    title
                )}
                {children}
            </Wrapper>
        )
    );
};

export default DrawerHeader;
